/**
 * Microphone recording for dictation and voice messages (desktop and mobile).
 * Browsers record in different formats (Chrome/Firefox: webm/opus, Safari: mp4/aac). For speech
 * recognition we decode the recording in the browser and send 16 kHz mono WAV, one format for all.
 * No browser speech API is used: Chrome's goes to Google, which is unreachable in China.
 */

export const MAX_RECORDING_MS = 120_000;

const CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2'];

export function recordingSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

function pickMime(): string | undefined {
  for (const m of CANDIDATES) if (MediaRecorder.isTypeSupported?.(m)) return m;
  return undefined;
}

export interface Recording {
  blob: Blob;
  /** Base MIME without parameters, e.g. "audio/webm". */
  mime: string;
  durationMs: number;
}

export class Recorder {
  private stream: MediaStream | null = null;
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private done: ((r: Recording | null) => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  /** Throws DOMException NotAllowedError / NotFoundError when the mic is blocked or missing. */
  async start(onAutoStop: () => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
    const mimeType = pickMime();
    this.rec = new MediaRecorder(this.stream, mimeType ? { mimeType, audioBitsPerSecond: 32_000 } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.onstop = () => {
      const type = this.rec?.mimeType || mimeType || 'audio/webm';
      const r: Recording | null = this.chunks.length
        ? { blob: new Blob(this.chunks, { type }), mime: type.split(';')[0]!, durationMs: Date.now() - this.startedAt }
        : null;
      this.cleanup();
      this.done?.(r);
      this.done = null;
    };
    try {
      this.ctx = new AudioContext();
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      this.ctx.createMediaStreamSource(this.stream).connect(this.analyser);
    } catch {
      /* level meter is optional */
    }
    this.startedAt = Date.now();
    this.rec.start(250);
    this.timer = setTimeout(onAutoStop, MAX_RECORDING_MS);
  }

  /** 0…1 input level for a simple meter. */
  level(): number {
    if (!this.analyser) return 0;
    const buf = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(buf);
    let peak = 0;
    for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
    return Math.min(1, peak / 64);
  }

  elapsedMs(): number {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  stop(): Promise<Recording | null> {
    return new Promise((resolve) => {
      if (!this.rec || this.rec.state === 'inactive') return resolve(null);
      this.done = resolve;
      this.rec.stop();
    });
  }

  cancel(): void {
    this.done = null;
    if (this.rec && this.rec.state !== 'inactive') {
      this.rec.onstop = () => this.cleanup();
      this.rec.stop();
    } else this.cleanup();
  }

  private cleanup(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.analyser = null;
  }
}

/** Decode any recording the browser made and re-encode as 16 kHz mono 16-bit WAV. */
export async function toWav16k(blob: Blob): Promise<Blob> {
  const data = await blob.arrayBuffer();
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(data);
  } finally {
    void ctx.close().catch(() => {});
  }
  const rate = 16000;
  const length = Math.max(1, Math.ceil(decoded.duration * rate));
  const off = new OfflineAudioContext(1, length, rate);
  const src = off.createBufferSource();
  src.buffer = decoded;
  src.connect(off.destination);
  src.start();
  const rendered = await off.startRendering();
  const pcm = rendered.getChannelData(0);
  const out = new DataView(new ArrayBuffer(44 + pcm.length * 2));
  const w = (o: number, s: string) => [...s].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF');
  out.setUint32(4, 36 + pcm.length * 2, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  out.setUint32(16, 16, true);
  out.setUint16(20, 1, true);
  out.setUint16(22, 1, true);
  out.setUint32(24, rate, true);
  out.setUint32(28, rate * 2, true);
  out.setUint16(32, 2, true);
  out.setUint16(34, 16, true);
  w(36, 'data');
  out.setUint32(40, pcm.length * 2, true);
  for (let i = 0; i < pcm.length; i++) {
    const v = Math.max(-1, Math.min(1, pcm[i]!));
    out.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([out.buffer], { type: 'audio/wav' });
}

export const fmtDuration = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
